// Foyer's replacement for nenda-autoplayer's src/lib/clients/NendaApiClient.ts.
//
// Not a port of the whole class - a narrow `ScheduleClient` interface
// covering exactly the five methods the ported machine actually calls
// (confirmed by direct grep against autoplayerMachine.ts/actions.ts, not
// guessed): `getScreenSchedule`, `getAdSlot`, `registerAdView`,
// `sessionAuth`, `ping` (via `checkConnectivity`). `NendaApiClient` also has
// `getScreens`/`resolveAsset`, used elsewhere in autoplayer's own app shell
// (a login screen, its own PLAYER-6-equivalent) but never by the state
// machine itself - out of scope here.
//
// When `autoplayerMachine.ts` itself is ported, its `nendaClient: NendaApiClient`
// parameter type should narrow to `ScheduleClient` below - a deliberate,
// documented delta from upstream, not an oversight.
import type { Schedule } from "../signage-engine/types/Schedule";
import type { Session } from "../signage-engine/types/Session";
import {
  legacyScheduleFromTimeline,
  type LegacyAsset,
  type LegacySchedule,
} from "../../server/services/legacySignageSchedule";
import type { SignageTimeline } from "../../shared/types/signage";

export interface ScheduleClient {
  getScreenSchedule(session: Session): Promise<Schedule>;
  /**
   * Confirmed unreachable given Foyer's adapter output: `Fetching ads` (the
   * only state that invokes this) requires `areAdsAllowed && shouldPlayAd`
   * (`guards.ts`) - `areAdsAllowed` reads `adPermissions.external
   * .fullTakeoverAllowed`, which `legacySignageSchedule.ts`'s
   * `NO_AD_PERMISSIONS` hardcodes `false` on every entry, and
   * `shouldPlayAd` requires an `activeSlot.type === ScheduleType.Ad`, which
   * this adapter never emits (no ad markers at all - see its own doc
   * comment). `Promise<never>` rather than a ported `AdSlot` type: this
   * never resolves, so nothing needs the shape.
   */
  getAdSlot(session: Session): Promise<never>;
  /** Transitively unreachable - only called from within the Ad-playback
   *  states `getAdSlot` above proves unreachable. */
  registerAdView(
    session: Session,
    renderPixel: string,
    adId: string
  ): Promise<never>;
  /**
   * Reachable, but SAFE to reject: `Initializing`'s own `"Authenticate
   * RedBee user"` state treats `onError` the same as `onDone` (both target
   * `"Fetch schedule"`) - confirmed by direct read of `autoplayerMachine.ts`
   * - logging a warning and continuing without a token. Foyer's live-TV DRM
   * is unrelated to this path (`src/tv/signage/playbackLicence.ts`, resolved
   * per-item, not per-session).
   */
  sessionAuth(session: Session): Promise<never>;
  /** Real implementation below, not a stub: Foyer's own `/healthcheck`. */
  ping(): Promise<Response>;
}

/**
 * `FoyerScheduleClient.getScreenSchedule` reads a local ref instead of
 * making a second network call - `SignageEngineSurface` already polls
 * Foyer's own timeline endpoint every 30s and writes the result here. This
 * keeps the ported machine's OWN trusted retry/backoff/circuit-breaker
 * timing (`fetchSchedule`'s actor, `scheduleFetchDelay`) driving a
 * data-source read rather than a network call, without hand-synthesizing
 * `xstate.done.actor.*` events to fight the actor's own lifecycle.
 */
export class FoyerScheduleClient implements ScheduleClient {
  constructor(
    private readonly getCurrentTimeline: () => SignageTimeline | null
  ) {}

  async getScreenSchedule(): Promise<Schedule> {
    const timeline = this.getCurrentTimeline();
    if (!timeline) {
      // The machine's own fetchSchedule actor retries on a rejection
      // (scheduleFetchDelay backs off on scheduleFetchFailures) - this is
      // the correct way to say "nothing to report yet", not a real error.
      throw new Error("no signage timeline resolved yet");
    }
    const legacy = legacyScheduleFromTimeline({
      timeline,
      assetBaseUrl: window.location.origin,
      // Foyer's own command execution (reboot/unpair/etc.) already runs
      // through App.tsx's handleEvent regardless of which signage engine is
      // mounted (confirmed: command/telemetry plumbing lives there, not in
      // any signage renderer) - embedding commands in the schedule payload
      // itself, the way the legacy HTTP facade does for a real fielded
      // legacy device with no other channel, would be redundant here.
      commands: [],
    });
    return normalizeLegacySchedule(legacy);
  }

  async getAdSlot(): Promise<never> {
    throw new Error(
      "Foyer signage has no ad product - getAdSlot should be unreachable (see ScheduleClient's doc comment)"
    );
  }

  async registerAdView(): Promise<never> {
    throw new Error(
      "Foyer signage has no ad product - registerAdView should be unreachable (see ScheduleClient's doc comment)"
    );
  }

  async sessionAuth(): Promise<never> {
    throw new Error(
      "Foyer has no RedBee/live-TV session auth on this path - live-TV DRM is resolved per-item via playbackLicence.ts. Safe to reject: autoplayerMachine's own Initializing state continues without a token on error."
    );
  }

  async ping(): Promise<Response> {
    return fetch("/healthcheck", { method: "GET", cache: "no-cache" });
  }
}

/**
 * `LegacySchedule` (Foyer's own wire type) and `Schedule` (the ported
 * machine's own type) are structurally near-identical but not assignable
 * without help: `LegacyAsset` carries `_id`, `Schedule`'s `MediaAsset`
 * expects `id` - exactly the normalisation upstream's own
 * `NendaApiClient.ts::transformSchedule` does for a real HTTP response
 * (`item.id || item._id`), which this bypasses by reading a local ref
 * instead of the network. Reproduced here since nothing upstream does it
 * for us on this path. `Playlist.assets`/`.channels`/`.signagePlaylist` are
 * optional in the ported `Schedule` type specifically because of this
 * asymmetry - see that type's own doc comment.
 */
function normalizeLegacySchedule(legacy: LegacySchedule): Schedule {
  const normalizeAsset = (asset: LegacyAsset) => ({
    ...asset,
    id: asset._id,
  });

  return {
    channels: [],
    signagePlaylists: legacy.signagePlaylists.map((playlist) => ({
      ...playlist,
      items: playlist.items.map(normalizeAsset),
    })),
    contentSchedule: legacy.contentSchedule,
    slotSchedule: legacy.slotSchedule,
    playlists: Object.fromEntries(
      Object.entries(legacy.playlists).map(([hash, entry]) => [
        hash,
        entry.type === "Signage" && "assets" in entry
          ? { ...entry, assets: entry.assets.map(normalizeAsset) }
          : entry,
      ])
    ),
    options: legacy.options,
    commands: [],
  } as unknown as Schedule;
}
